import * as React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const ErrorContent = ({ error, onReset }: { error: Error; onReset: () => void }) => {
  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-lg mx-4">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex size-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="size-5 text-destructive" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">
                Something went wrong
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            An unexpected error occurred. The issue has been logged.
          </p>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onReset}
              className="gap-2"
            >
              <RefreshCw className="size-3.5" />
              Try Again
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.location.reload()}
              className="gap-2"
            >
              <RefreshCw className="size-3.5" />
              Reload Page
            </Button>
          </div>

          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {showDetails ? "Hide" : "Show"} Technical Details
            </button>

            {showDetails && (
              <div className="mt-3 rounded-md bg-muted/50 p-3">
                <p className="text-xs font-mono text-destructive mb-2 break-words">
                  {error?.message || "Unknown error"}
                </p>
                {error?.stack && (
                  <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words overflow-auto max-h-40">
                    {error.stack}
                  </pre>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const handler = (event: ErrorEvent) => {
      event.preventDefault();
      setError(event.error || new Error(event.message));
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  if (error) {
    return <ErrorContent error={error} onReset={() => setError(null)} />;
  }

  return <>{children}</>;
}

export { ErrorBoundary, ErrorContent };
